import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import Theme from 'assets/themes/Theme.Common';
import { roundToStep } from 'assets/utils/data/Number';
import { generateRandomUid } from 'assets/utils/data/String';
import { HttpStatus } from 'config/Api.Config';
import { cloneDeep, isEmpty, merge, set, sum, values } from 'lodash';
import AccountingItemTypeCode from 'models/enums/AccountingItemTypeCode';
import ContractRefundStatusCode from 'models/enums/ContractRefundStatusCode';
import FundingSourceCode from 'models/enums/FundingSourceCode';
import PaymentPurposeCode from 'models/enums/PaymentPurposeCode';
import ResponsibilityTypeCode from 'models/enums/ResponsibilityTypeCode';
import ContractRefundPaymentItem from 'models/finance/contractRefundPaymentItem/Model.ContractRefundPaymentItem';
import Contract from 'models/sales/contract/Model.Contract';
import contractSetRefundedStatusApi from 'models/sales/contract/setRefundedStatus/Api.Contract.SetRefundedStatus';
import ContractRefund from 'models/sales/contractRefund/Model.ContractRefund';
import contractRefundSetStatusApi from 'models/sales/contractRefund/setStatus/Api.ContractRefund.SetStatus';
import ContractRefundDealerPaymentControl from 'models/sales/contractRefundDealerPaymentControl/Model.ContractRefundDealerPaymentControl';
import contractRefundDealerPaymentControlSaveApi from 'models/sales/contractRefundDealerPaymentControl/save/Api.ContractRefundDealerPaymentControl.Save';
import ContractRefundInvoice from 'models/sales/contractRefundInvoice/Model.ContractRefundInvoice';
import { ContractRefundCostControlsSummaryTable } from '../../components/ContractRefundCostControlsSummaryTable';
import { ExcludedAmount } from '../../components/ExcludedAmount';
import { useContractRefundProvider } from '../../Sales.ContractRefund.Hooks';
import SalesContractRefundInfoNotes from '../info/Sales.ContractRefund.Info.Notes';

type DealerPaymentControlsForm = {
  dealerPaymentControls: Array<Api.Sales.ContractRefundDealerPaymentControl.Save.IRequest & { rowUid?: string }>;
};
type DealerPaymentControlRow = DealerPaymentControlsForm['dealerPaymentControls'][number];

const SalesContractRefundProcessDealerPaymentControls = () => {
  const { lang } = getLocales();
  const { userId } = useAuthStore();
  const { pageRouter, listApi } = useContractRefundProvider();

  const { payload: contractRefund } = useApi(
    {
      action: ContractRefund.find,
      wait: !pageRouter?.params?.id,
      body: { id: Number(pageRouter?.params?.id) },
    },
    [pageRouter?.params?.id]
  );
  const contractId = contractRefund?.contractCancellation?.contractId;
  const { payload: contract } = useApi(
    {
      action: Contract.find,
      wait: !contractId,
      body: { id: contractId },
    },
    [contractId]
  );

  // The API returns the real amount for excluded profit lines, so excluded amounts must not be allocated
  const allocatableASCDealerProfitTotal = contractRefund?.isRefundedASCDealerProfitTotalExcluded
    ? 0
    : (contractRefund?.refundedASCDealerProfitTotal ?? 0);
  const allocatableDealerProfitTotal = contractRefund?.isRefundedDealerProfitTotalExcluded
    ? 0
    : (contractRefund?.refundedDealerProfitTotal ?? 0);

  const refundedDealerCostAndASCProfitTotal =
    (contractRefund?.refundedDealerCostTotalLessDeductions ?? 0) + allocatableASCDealerProfitTotal;
  const dealerProtectProfitAmount =
    (contractRefund?.refundConfigTemplateJson ?? contractRefund?.refundConfigTemplate)?.dealerProtectProfitAmount ?? 0;
  const minimumDealerCostAllocation = roundToStep(
    Math.max(refundedDealerCostAndASCProfitTotal - dealerProtectProfitAmount, 0),
    0.01
  );
  const isNoRefund = !!contractRefund && refundedDealerCostAndASCProfitTotal <= 0 && allocatableDealerProfitTotal <= 0;

  return (
    <ModalForm<DealerPaymentControlsForm>
      subcomponents={merge(cloneDeep(ThemeModalFormDefault), {
        modal: { page: { style: { pageWrapper: { ['--modalWidth']: 'var(--sizeXXL) * 1.5' } } } },
      })}
      dependencyArray={[contractRefund]}
      title={lang.dealerPaymentControls}
      default={{ dealerPaymentControls: contractRefund?.dealerPaymentControls ?? [] }}
      onCancel={() => {
        pageRouter.updateParams({});
      }}
      onSubmit={async ({ dealerPaymentControls }) => {
        if (!contract) {
          addLog({ error: lang.saveError });
          return;
        }

        if (isNoRefund) {
          const refundStatusRes = await contractRefundSetStatusApi({
            id: contractRefund?.id,
            newStatus: ContractRefundStatusCode.PAID_NOREFUND,
          });
          if (refundStatusRes.status !== HttpStatus.ok) {
            addLog({ error: refundStatusRes.message });
          } else {
            const refundedStatusRes = await contractSetRefundedStatusApi({ contractId: contract.id });
            if (refundedStatusRes.status !== HttpStatus.ok) addLog({ error: refundedStatusRes.message });
            else addLog({ success: lang.saveSuccess });
          }
          listApi.execute((body) => body);
          pageRouter.updateParams({});
          return;
        }

        const resolvedDealerPaymentControls = (dealerPaymentControls || []).map(({ ...control }) => ({
          ...control,
          id: control.id || 0,
          contractRefundId: contractRefund?.id,
        }));

        let hasSaveFailed = false;
        const newlyCreatedControlIds: number[] = [];
        for (const control of resolvedDealerPaymentControls) {
          const isNewControl = !control.id;
          const savedControlRes = await contractRefundDealerPaymentControlSaveApi(control);
          if (savedControlRes.status !== HttpStatus.ok) {
            hasSaveFailed = true;
            addLog({ error: savedControlRes.message });
          }
          if (savedControlRes.payload && savedControlRes.status === HttpStatus.ok && isNewControl) {
            newlyCreatedControlIds.push(savedControlRes.payload);
          }
        }

        if (!isEmpty(newlyCreatedControlIds)) {
          const dealerPaymentControlsApi = await ContractRefundDealerPaymentControl.list({
            id: newlyCreatedControlIds,
          });

          if (dealerPaymentControlsApi.payload && dealerPaymentControlsApi.status === HttpStatus.ok) {
            for (const dealerPaymentControl of dealerPaymentControlsApi.payload?.data ?? []) {
              if (dealerPaymentControl.accountingItemTypeCode === AccountingItemTypeCode.AccountPayable) {
                const paymentItemRes = await ContractRefundPaymentItem.save({
                  ...dealerPaymentControl.toAccountingItem<Model.IContractRefundPaymentItem>(contract),
                  createdById: userId,
                });
                if (paymentItemRes.status !== HttpStatus.ok) {
                  hasSaveFailed = true;
                  addLog({ error: paymentItemRes.message });
                }
              } else if (
                dealerPaymentControl.accountingItemTypeCode === AccountingItemTypeCode.AccountReceivable &&
                dealerPaymentControl.paymentPurposeCode !== PaymentPurposeCode.PAID_TO_CUSTOMER_BY_DEALER
              ) {
                const invoiceRes = await ContractRefundInvoice.save({
                  ...dealerPaymentControl.toAccountingItem<Model.IContractRefundInvoice>(contract),
                  createdById: userId,
                });
                if (invoiceRes.status !== HttpStatus.ok) {
                  hasSaveFailed = true;
                  addLog({ error: invoiceRes.message });
                }
              } else {
                // Do nothing
              }
            }
          } else {
            hasSaveFailed = true;
            addLog({ error: dealerPaymentControlsApi.message });
          }

          if (!hasSaveFailed) {
            const refundedStatusRes = await contractSetRefundedStatusApi({ contractId: contract.id });
            if (refundedStatusRes.status !== HttpStatus.ok) {
              hasSaveFailed = true;
              addLog({ error: refundedStatusRes.message });
            } else {
              const refundStatusRes = await contractRefundSetStatusApi({
                id: contractRefund?.id,
                newStatus: ContractRefundStatusCode.PAYMENT_PENDING,
              });
              if (refundStatusRes.status !== HttpStatus.ok) {
                hasSaveFailed = true;
                addLog({ error: refundStatusRes.message });
              }
            }
          }
        }

        if (!hasSaveFailed) addLog({ success: lang.saveSuccess });
        listApi.execute((body) => body);
        pageRouter.updateParams({});
      }}
      validation={(data, errors) => {
        if (isNoRefund) return;

        if (
          data?.dealerPaymentControls?.some(
            (it) =>
              it.contractRefundPayeeId == null ||
              !it.fundingSourceCode ||
              !it.responsibilityTypeCode ||
              !it.paymentPurposeCode ||
              (it.allocatedAmountCAD ?? 0) < 0
          )
        ) {
          set(errors, 'dealerPaymentControls._objectError', lang.required);
        }

        if (
          !data?.dealerPaymentControls?.some(
            (it) => it.accountingItemTypeCode === AccountingItemTypeCode.AccountPayable
          )
        ) {
          set(errors, 'dealerPaymentControls._objectError', lang.atLeastOneAccountPayableRequired);
        }

        const editedDealerCostTotal = roundToStep(
          sum(
            (data?.dealerPaymentControls ?? [])
              .filter((it) => it.accountingItemTypeCode === AccountingItemTypeCode.AccountPayable)
              .map((it) => it.allocatedAmountCAD ?? 0)
          ),
          0.01
        );
        const editedDealerProfitTotal = roundToStep(
          sum(
            (data?.dealerPaymentControls ?? [])
              .filter((it) => it.accountingItemTypeCode !== AccountingItemTypeCode.AccountPayable)
              .map((it) => it.allocatedAmountCAD ?? 0)
          ),
          0.01
        );

        const refundedDealerProfitTotal = roundToStep(allocatableDealerProfitTotal, 0.01);
        if (editedDealerCostTotal < minimumDealerCostAllocation)
          set(
            errors,
            'dealerPaymentControls._objectError',
            lang.mustBeEqualOrMoreThanAmount.replace('{amount}', `$${minimumDealerCostAllocation}`)
          );
        if (refundedDealerProfitTotal >= 0 && editedDealerProfitTotal > refundedDealerProfitTotal)
          set(
            errors,
            'dealerPaymentControls._objectError',
            lang.mustBeEqualOrLessThanAmount.replace('{amount}', `$${refundedDealerProfitTotal}`)
          );
      }}
      render={({ form }) => {
        const payees = contractRefund?.contractRefundPayees ?? [];
        const rows = form.data?.dealerPaymentControls ?? [];

        const updateRow = (rowKey: string | number, updated: Partial<DealerPaymentControlRow>) => {
          const updatedRows = rows.map((it) => ((it.rowUid ?? it.id) === rowKey ? { ...it, ...updated } : it));
          form.update({ dealerPaymentControls: updatedRows });
        };
        const addRow = (accountingItemTypeCode: Model.Enum.AccountingItemTypeCode | null) => {
          const isAccountPayable = accountingItemTypeCode === AccountingItemTypeCode.AccountPayable;
          form.update({
            dealerPaymentControls: [
              ...rows,
              {
                id: 0,
                rowUid: generateRandomUid(),
                contractRefundId: contractRefund?.id,
                contractRefundPayeeId: undefined,
                allocatedAmountCAD: 0,
                fundingSourceCode: isAccountPayable ? FundingSourceCode.DEALER_COST : FundingSourceCode.DEALER_PROFIT,
                responsibilityTypeCode: undefined,
                paymentPurposeCode: undefined,
                accountingItemTypeCode,
              },
            ],
          });
        };
        const deleteRow = (rowKey: string | number) => {
          form.update({ dealerPaymentControls: rows.filter((it) => (it.rowUid ?? it.id) !== rowKey) });
        };

        const costRows = rows.filter((row) => row.accountingItemTypeCode === AccountingItemTypeCode.AccountPayable);
        const profitRows = rows.filter((row) => row.accountingItemTypeCode !== AccountingItemTypeCode.AccountPayable);

        const profitPayees = payees.filter((payee) => payee.dealerId);

        const dealerCostAllocated = sum(costRows.map((row) => row.allocatedAmountCAD ?? 0));
        const dealerProfitAllocated = sum(profitRows.map((row) => row.allocatedAmountCAD ?? 0));
        const dealerCostRemaining = roundToStep(minimumDealerCostAllocation - dealerCostAllocated, 0.01);
        const dealerProfitRemaining = roundToStep(allocatableDealerProfitTotal - dealerProfitAllocated, 0.01);
        // const dealerTotalRemaining = roundToStep(
        //   (contractRefund?.refundedDealerTotal ?? 0) - (dealerCostAllocated + dealerProfitAllocated),
        //   0.01
        // );
        const hasAccountPayableControl = rows.some(
          (row) => row.accountingItemTypeCode === AccountingItemTypeCode.AccountPayable
        );
        const isDealerCostControlInvalid = !hasAccountPayableControl || dealerCostRemaining > 0;

        return (
          <FormWrapper>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <DropdownButton
                title={lang.addControl}
                buttonBody={lang.addControl}
                buttonMedia={<Icon class="fas-plus" />}
                horizontalPosition="auto_fixed"
                verticalPosition="auto_fixed"
                subcomponents={ThemeDropdownButtonPopup}
                dropdownOptions={
                  <>
                    <Button
                      class={ThemeButtonMenuItem}
                      onClick={() => addRow(AccountingItemTypeCode.AccountPayable)}
                      render={lang.dealerCostControl}
                      disabled={!(refundedDealerCostAndASCProfitTotal > 0)}
                      media={<Icon class="fas-plus" />}
                    />
                    <Button
                      class={ThemeButtonMenuItem}
                      onClick={() => addRow(AccountingItemTypeCode.AccountReceivable)}
                      render={lang.dealerProfitControl}
                      disabled={profitRows.length > 0 || !(allocatableDealerProfitTotal > 0)}
                      media={<Icon class="fas-plus" />}
                    />
                  </>
                }
              />
            </div>
            &nbsp;
            <ContractRefundCostControlsSummaryTable contractRefund={contractRefund} />
            &nbsp;
            <table className={ThemeTableSimple}>
              <thead>
                <tr>
                  <th />
                  <th style={{ textAlign: 'center' }}>
                    {lang.refundedDealerCostAndASCDealerProfitResponsibilityTotalAmt}
                  </th>
                  <th style={{ textAlign: 'center' }}>{lang.refundedDealerProfitTotalAmt}</th>
                  {/* <th style={{ textAlign: 'center' }}>{lang.totalRefundedSubtotal}</th> */}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>{lang.total}</td>
                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    <FormatValue value={`$${roundToStep(refundedDealerCostAndASCProfitTotal, 0.01)}`} />
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    <ExcludedAmount isExcluded={contractRefund?.isRefundedDealerProfitTotalExcluded}>
                      <FormatValue value={`$${contractRefund?.refundedDealerProfitTotal ?? 0}`} />
                    </ExcludedAmount>
                  </td>
                  {/* <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    <FormatValue value={`$${contractRefund?.refundedDealerTotal ?? 0}`} />
                  </td> */}
                </tr>
                <tr style={{ color: Theme.Color.error }}>
                  <td style={{ fontWeight: 'bold', color: Theme.Color.error }}>{lang.remaining}</td>
                  <td style={{ textAlign: 'center', color: Theme.Color.error, fontWeight: 'bold' }}>
                    <FormatValue value={`$${roundToStep(dealerCostRemaining, 0.01)}`} />
                  </td>
                  <td style={{ textAlign: 'center', color: Theme.Color.error, fontWeight: 'bold' }}>
                    <FormatValue value={`$${roundToStep(dealerProfitRemaining, 0.01)}`} />
                  </td>
                  {/* <td style={{ textAlign: 'center', color: Theme.Color.error, fontWeight: 'bold' }}>
                    <FormatValue value={`$${dealerTotalRemaining}`} />
                  </td> */}
                </tr>
              </tbody>
            </table>
            {isDealerCostControlInvalid && (
              <p style={{ color: Theme.Color.error, textAlign: 'center' }}>
                *{lang.dealerCostControlFullAmountRequired}
              </p>
            )}
            &nbsp;
            {costRows.length > 0 && (
              <DealerPaymentControlSection
                title={lang.dealerCostControls}
                accountingItemTypeCode={AccountingItemTypeCode.AccountPayable}
                rows={costRows}
                payees={payees}
                accountingItemOptions={[
                  {
                    id: AccountingItemTypeCode.AccountPayable,
                    title: AccountingItemTypeCode.AccountPayable,
                  },
                ]}
                fundingSourceOptions={[FundingSourceCode.DEALER_COST, FundingSourceCode.ASC_DEALER_PROFIT]}
                updateRow={updateRow}
                deleteRow={deleteRow}
                addRow={addRow}
              />
            )}
            &nbsp;
            {profitRows.length > 0 && (
              <DealerPaymentControlSection
                title={lang.dealerProfitControls}
                accountingItemTypeCode={AccountingItemTypeCode.AccountReceivable}
                rows={profitRows}
                payees={profitPayees}
                accountingItemOptions={[
                  {
                    id: AccountingItemTypeCode.AccountReceivable,
                    title: AccountingItemTypeCode.AccountReceivable,
                  },
                ]}
                fundingSourceOptions={[FundingSourceCode.DEALER_PROFIT]}
                updateRow={updateRow}
                deleteRow={deleteRow}
                addRow={addRow}
              />
            )}
            &nbsp;
            <SalesContractRefundInfoNotes />
          </FormWrapper>
        );
      }}
      submitButtonText={isNoRefund ? lang.processRefundWithoutPayment : lang.processRefund}
    />
  );
};
const getPayeeLabel = (payee: Model.IContractRefundPayee): string => {
  const { lang } = getLocales();
  const kind =
    payee.dealer || payee.dealerId
      ? lang.dealer
      : payee.lender || payee.lenderId
        ? lang.lender
        : payee.client || payee.clientId
          ? lang.client
          : lang.payee;
  const name = payee.dealer?.name ?? payee.lender?.name ?? payee.client?.name ?? payee.payee?.name;
  const primary = payee.isPrimary ? ` (${lang.primaryPayee})` : '';
  return `${kind}${name ? `: ${name}` : ''}${primary}`;
};

const DealerPaymentControlSection = ({
  title,
  accountingItemTypeCode,
  rows,
  payees,
  accountingItemOptions,
  fundingSourceOptions,
  updateRow,
  deleteRow,
  addRow,
}: {
  title: string;
  accountingItemTypeCode: Model.Enum.AccountingItemTypeCode | null;
  rows: DealerPaymentControlRow[];
  payees: Model.IContractRefundPayee[];
  accountingItemOptions: Array<{ id: string; title: string }>;
  fundingSourceOptions: Model.Enum.FundingSourceCode[];
  updateRow: (rowKey: string | number, updated: Partial<DealerPaymentControlRow>) => void;
  deleteRow: (rowKey: string | number) => void;
  addRow: (accountingItemTypeCode: Model.Enum.AccountingItemTypeCode | null) => void;
}) => {
  const { lang, translate } = getLocales();
  const isAccountPayableSection = accountingItemTypeCode === AccountingItemTypeCode.AccountPayable;
  return (
    <>
      <h3>{title.toLocaleUpperCase()}</h3>
      <table className={ThemeTableSimple}>
        <thead>
          <tr>
            <th>{lang.payee}</th>
            <th>{lang.responsibilityType}</th>
            <th>{lang.paymentPurpose}</th>
            <th>{lang.accountingItemType}</th>
            <th>{lang.fundingSource}</th>
            <th style={{ textAlign: 'center' }}>{lang.allocatedAmount}</th>
            {isAccountPayableSection && <th>{lang.excluded}</th>}
            <th />
          </tr>
        </thead>
        <tbody>
          <DataMap
            data={rows}
            render={({ data: row }) => {
              const rowKey = row.rowUid ?? row.id;
              return (
                <tr key={rowKey}>
                  <td>
                    <SelectInput
                      name="contractRefundPayeeId"
                      data={payees.map((payee) => ({
                        id: payee.id,
                        title: getPayeeLabel(payee),
                      }))}
                      value={row.contractRefundPayeeId}
                      onChange={({ contractRefundPayeeId }) => updateRow(rowKey, { contractRefundPayeeId })}
                      error={row.contractRefundPayeeId == null && lang.required}
                    />
                  </td>
                  <td>
                    <SelectInput
                      name="responsibilityTypeCode"
                      data={values(ResponsibilityTypeCode).map((it) => ({ id: it, title: translate(it) }))}
                      value={row.responsibilityTypeCode}
                      onChange={({ responsibilityTypeCode }) => updateRow(rowKey, { responsibilityTypeCode })}
                      error={!row.responsibilityTypeCode && lang.required}
                    />
                  </td>
                  <td>
                    <SelectInput
                      name="paymentPurposeCode"
                      data={values(PaymentPurposeCode).map((it) => ({ id: it, title: translate(it) }))}
                      value={row.paymentPurposeCode}
                      onChange={({ paymentPurposeCode }) => updateRow(rowKey, { paymentPurposeCode })}
                      error={!row.paymentPurposeCode && lang.required}
                    />
                  </td>
                  <td>
                    <SelectInput
                      name="accountingItemTypeCode"
                      data={accountingItemOptions}
                      value={row.accountingItemTypeCode}
                      disabled={isAccountPayableSection}
                      onChange={({ accountingItemTypeCode }) =>
                        updateRow(rowKey, {
                          accountingItemTypeCode: accountingItemTypeCode,
                        })
                      }
                    />
                  </td>
                  <td>
                    <SelectInput
                      name="fundingSourceCode"
                      data={fundingSourceOptions.map((it) => ({ id: it, title: translate(it) }))}
                      value={row.fundingSourceCode}
                      disabled={!isAccountPayableSection}
                      onChange={({ fundingSourceCode }) => updateRow(rowKey, { fundingSourceCode })}
                      error={!row.fundingSourceCode && lang.required}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <NumberInput
                      name="allocatedAmountCAD"
                      value={row.allocatedAmountCAD}
                      onChange={({ allocatedAmountCAD }) => updateRow(rowKey, { allocatedAmountCAD })}
                      icon={<Icon class="fas-dollar-sign" />}
                      min={0}
                      step={0.01}
                      roundOnBlur
                      error={(row.allocatedAmountCAD ?? 0) < 0 && lang.required}
                    />
                  </td>
                  {isAccountPayableSection && (
                    <td>
                      <CheckInput
                        name="isExcluded"
                        class={inputStyles.check.switch}
                        value={row.isExcluded}
                        onChange={({ isExcluded }) => updateRow(rowKey, { isExcluded: !!isExcluded })}
                        htmlElementProps={{ title: row.isExcluded ? lang.disable : lang.enable }}
                      />
                    </td>
                  )}
                  <td>
                    <Button
                      class={ThemeButtonCircle}
                      onClick={() => deleteRow(rowKey)}
                      media={<Icon class="fas-trash" />}
                    />
                  </td>
                </tr>
              );
            }}
          />
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={isAccountPayableSection ? 8 : 7} style={{ textAlign: 'end' }}>
              <Button
                class={ThemeButtonCircle}
                onClick={() => addRow(accountingItemTypeCode)}
                media={<Icon class="fas-plus" />}
                render={lang.addControl}
              />
            </td>
          </tr>
        </tfoot>
      </table>
    </>
  );
};

export default SalesContractRefundProcessDealerPaymentControls;
